/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Activity, Beef, Stethoscope, Heart, Wheat, Calendar as CalIcon,
  BarChart3, MessageCircle, Search as SearchIcon, Clock, Mail,
  CheckCircle2, AlertCircle, Sparkles, TrendingUp, LogIn,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODULE_META = {
  animals:      { label: "Registre",     icon: Beef,        color: "#1C3A27" },
  health:       { label: "Santé",        icon: Stethoscope, color: "#C84B31" },
  reproduction: { label: "Reproduction", icon: Heart,       color: "#E05A3D" },
  nutrition:    { label: "Nutrition",    icon: Wheat,       color: "#7A5A1C" },
  calendar:     { label: "Calendrier",   icon: CalIcon,     color: "#1E6BBA" },
  analytics:    { label: "Statistiques", icon: BarChart3,   color: "#1C7A3C" },
  expert:       { label: "Expert",       icon: MessageCircle, color: "#D97706" },
  clients:      { label: "Clients",      icon: Activity,    color: "#5C6B5C" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function relativeDays(days) {
  if (days === null || days === undefined) return "Jamais";
  if (days <= 0) return "Aujourd'hui";
  if (days === 1) return "Hier";
  return `Il y a ${days} jour${days > 1 ? "s" : ""}`;
}

export default function ClientActivityModal({ open, onOpenChange, clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !clientId) return;
    setLoading(true);
    setError("");
    setData(null);
    axios
      .get(`${API}/admin/clients/${clientId}/activity`, { withCredentials: true })
      .then((res) => setData(res.data))
      .catch((err) => setError(err?.response?.data?.detail || "Erreur de chargement"))
      .finally(() => setLoading(false));
  }, [open, clientId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="client-activity-modal"
        className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[#FAFAF6]"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-2xl text-[#1C3A27]">
            <Activity size={22} className="text-[#E05A3D]" />
            Profil d'activité client
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-10 text-center text-[#5C6B5C]">Chargement…</div>
        )}
        {error && (
          <div className="py-10 text-center text-red-600">{error}</div>
        )}

        {data && !loading && (
          <div className="space-y-5 mt-2">
            <ClientHeader client={data.client} />
            <CounterGrid counters={data.counters} />
            <ModuleVisits visits={data.module_visits} />
            <RecentSearches searches={data.searches} />
            <ExpertHistory items={data.expert_requests} />
            <Timeline events={data.timeline} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ClientHeader({ client }) {
  const [busy, setBusy] = useState(false);

  const handleImpersonate = async () => {
    if (!window.confirm(
      `Vous allez vous connecter en tant que ${client.name} (${client.email}) pour lui apporter de l'aide.\n\n` +
      `Une bannière orange s'affichera en haut de chaque écran. Vous pourrez revenir à votre compte à tout moment.\n\n` +
      `Continuer ?`
    )) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/clients/${client.id}/impersonate`, {}, { withCredentials: true });
      toast.success("Connecté en tant que " + client.name);
      // Hard reload to the client dashboard so the new auth context loads
      window.location.href = "/dashboard/client";
    } catch (e) {
      const msg = e.response?.data?.detail || "Connexion impossible.";
      toast.error(msg);
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl bg-gradient-to-br from-[#1C3A27] to-[#142A1C] text-white p-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-xl font-bold">{client.name}</h3>
            {client.self_signup && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E05A3D]/20 text-[#FF8C6E] text-[10px] font-bold uppercase border border-[#E05A3D]/40">
                <Sparkles size={10} /> Auto-inscrit
              </span>
            )}
            {client.is_trial && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-200 text-[10px] font-bold uppercase border border-amber-400/40">
                Essai
              </span>
            )}
          </div>
          <p className="text-white/70 text-sm mt-0.5 flex items-center gap-1.5">
            <Mail size={12} /> {client.email}
          </p>
          <button
            type="button"
            data-testid="impersonate-client-btn"
            onClick={handleImpersonate}
            disabled={busy || client.status !== "active"}
            title={client.status !== "active" ? "Le compte client doit être actif" : ""}
            className="mt-3 inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-[#E05A3D] hover:bg-[#C84B31] disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-bold uppercase tracking-wide transition-colors"
          >
            <LogIn size={14} />
            {busy ? "Connexion…" : "Se connecter en tant que ce client"}
          </button>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <span className="px-2.5 py-1 rounded-full bg-white/10 border border-white/20 capitalize">
              Plan {client.plan}
            </span>
            <span className="px-2.5 py-1 rounded-full bg-white/10 border border-white/20">
              {client.billing_cycle === "annual" ? "Annuel" : "Mensuel"}
            </span>
            <span className="px-2.5 py-1 rounded-full bg-white/10 border border-white/20 capitalize">
              {client.status}
            </span>
          </div>
        </div>
        <div className="text-end text-xs space-y-1">
          <p className="text-white/60 uppercase tracking-wider font-semibold">Inscrit</p>
          <p className="text-white font-bold">{relativeDays(client.days_since_signup)}</p>
          <p className="text-white/60 uppercase tracking-wider font-semibold mt-2">Dernière connexion</p>
          <p className="text-white font-bold">{relativeDays(client.days_since_login)}</p>
        </div>
      </div>
    </div>
  );
}

function CounterCard({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-xl border bg-white p-3" style={{ borderColor: `${color}25` }}>
      <div className="flex items-center justify-between">
        <Icon size={16} style={{ color }} />
        <span className="font-display text-2xl font-black" style={{ color }}>{value}</span>
      </div>
      <p className="text-[11px] text-[#5C6B5C] uppercase tracking-wider mt-1 font-semibold">{label}</p>
    </div>
  );
}

function CounterGrid({ counters }) {
  const items = [
    { key: "animals",             label: "Animaux",     icon: Beef,        color: "#1C3A27" },
    { key: "health_records",      label: "Soins",       icon: Stethoscope, color: "#C84B31" },
    { key: "reproduction_events", label: "Reproduction", icon: Heart,      color: "#E05A3D" },
    { key: "feedings",            label: "Rations",     icon: Wheat,       color: "#7A5A1C" },
    { key: "husbandry_events",    label: "Pesées/Suivi", icon: TrendingUp, color: "#1E6BBA" },
    { key: "expert_requests",     label: "Questions Expert", icon: MessageCircle, color: "#D97706" },
  ];
  return (
    <div>
      <h4 className="text-xs font-bold uppercase tracking-wider text-[#5C6B5C] mb-2">Engagement</h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
        {items.map((it) => (
          <CounterCard
            key={it.key}
            icon={it.icon}
            label={it.label}
            value={counters[it.key] ?? 0}
            color={it.color}
          />
        ))}
      </div>
    </div>
  );
}

function ModuleVisits({ visits }) {
  const max = Math.max(1, ...visits.map((v) => v.count));
  return (
    <div className="rounded-2xl border border-[#E2E4DF] bg-white p-4">
      <h4 className="text-xs font-bold uppercase tracking-wider text-[#5C6B5C] mb-3 flex items-center gap-1.5">
        <Activity size={12} /> Modules explorés
      </h4>
      {visits.length === 0 ? (
        <p className="text-sm text-[#A8AFA1] italic">Aucune visite enregistrée pour le moment.</p>
      ) : (
        <div className="space-y-2">
          {visits.map((v) => {
            const meta = MODULE_META[v.module] || { label: v.module, icon: Activity, color: "#5C6B5C" };
            const Icon = meta.icon;
            const pct = (v.count / max) * 100;
            return (
              <div key={v.module} className="flex items-center gap-3">
                <div className="flex items-center gap-2 w-32 flex-shrink-0">
                  <Icon size={14} style={{ color: meta.color }} />
                  <span className="text-sm font-semibold text-[#1C3A27]">{meta.label}</span>
                </div>
                <div className="flex-1 h-2.5 bg-[#F0F0EB] rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: meta.color }} />
                </div>
                <span className="text-xs font-bold text-[#1C3A27] w-10 text-end">{v.count}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RecentSearches({ searches }) {
  return (
    <div className="rounded-2xl border border-[#E2E4DF] bg-white p-4">
      <h4 className="text-xs font-bold uppercase tracking-wider text-[#5C6B5C] mb-3 flex items-center gap-1.5">
        <SearchIcon size={12} /> Recherches dans l'app
      </h4>
      {searches.length === 0 ? (
        <p className="text-sm text-[#A8AFA1] italic">Aucune recherche enregistrée.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {searches.map((s, i) => {
            const meta = MODULE_META[s.module] || { label: s.module, color: "#5C6B5C" };
            return (
              <div
                key={`${s.module}-${s.query}-${i}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border bg-[#FAFAF6] text-xs"
                style={{ borderColor: `${meta.color}40` }}
                title={`${meta.label} · ${fmtDate(s.at)}`}
              >
                <span className="font-bold" style={{ color: meta.color }}>"{s.query}"</span>
                <span className="text-[#5C6B5C]">· {meta.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ExpertHistory({ items }) {
  return (
    <div className="rounded-2xl border border-[#E2E4DF] bg-white p-4">
      <h4 className="text-xs font-bold uppercase tracking-wider text-[#5C6B5C] mb-3 flex items-center gap-1.5">
        <MessageCircle size={12} /> Demandes à l'expert ({items.length})
      </h4>
      {items.length === 0 ? (
        <p className="text-sm text-[#A8AFA1] italic">Aucune demande envoyée.</p>
      ) : (
        <div className="space-y-2">
          {items.map((r) => (
            <div key={r.id} className="rounded-xl border border-[#E2E4DF] p-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded-full bg-[#1C3A27]/10 text-[#1C3A27] text-[10px] font-bold uppercase">
                    {r.type}
                  </span>
                  {r.status === "handled" ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-[#1C7A3C]">
                      <CheckCircle2 size={11} /> Traité
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-amber-700">
                      <AlertCircle size={11} /> En attente
                    </span>
                  )}
                </div>
                <span className="text-xs text-[#5C6B5C]">{fmtDate(r.created_at)}</span>
              </div>
              {r.subject && <p className="mt-1.5 text-sm font-semibold text-[#1C3A27]">{r.subject}</p>}
              {r.message && <p className="mt-0.5 text-xs text-[#5C6B5C] line-clamp-2">{r.message}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Timeline({ events }) {
  return (
    <div className="rounded-2xl border border-[#E2E4DF] bg-white p-4">
      <h4 className="text-xs font-bold uppercase tracking-wider text-[#5C6B5C] mb-3 flex items-center gap-1.5">
        <Clock size={12} /> Activité récente
      </h4>
      {events.length === 0 ? (
        <p className="text-sm text-[#A8AFA1] italic">Aucune activité tracée pour le moment.</p>
      ) : (
        <ol className="space-y-2">
          {events.map((e) => {
            const meta = MODULE_META[e.module] || { label: e.module, icon: Activity, color: "#5C6B5C" };
            const Icon = meta.icon;
            return (
              <li key={e.id} className="flex items-start gap-3 text-sm">
                <div className="w-7 h-7 rounded-lg grid place-items-center flex-shrink-0" style={{ backgroundColor: `${meta.color}15` }}>
                  <Icon size={14} style={{ color: meta.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[#1C3A27]">
                    {e.type === "search" ? (
                      <>A recherché <strong>"{e.query}"</strong> dans <strong>{meta.label}</strong></>
                    ) : (
                      <>A ouvert <strong>{meta.label}</strong></>
                    )}
                  </p>
                  <p className="text-[11px] text-[#A8AFA1]">{fmtDate(e.created_at)}</p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
